import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import enCommon from './i18n/en/common.json';
import arCommon from './i18n/ar/common.json';

export type AppLocale = 'en' | 'ar';
export type TextDirection = 'ltr' | 'rtl';

export interface LocaleMeta {
  /** BCP-47 code used by i18next and the <html lang> attribute. */
  code: AppLocale;
  /** Name shown in the language switcher, written in the language itself. */
  label: string;
  /** Short tag for compact toggles. */
  short: string;
  /** Writing direction the locale forces on <html dir>. */
  dir: TextDirection;
}

const EN_LOCALE: LocaleMeta = {
  code: 'en',
  label: 'English',
  short: 'EN',
  dir: 'ltr',
};
const AR_LOCALE: LocaleMeta = {
  code: 'ar',
  label: 'العربية',
  short: 'ع',
  dir: 'rtl',
};

/** Every locale the apps ship. Adding one here + an i18n/<code> folder is
 *  all that a new language needs at the infrastructure level. */
export const SUPPORTED_LOCALES: readonly LocaleMeta[] = [EN_LOCALE, AR_LOCALE];

const DEFAULT_LOCALE: AppLocale = 'en';
const STORAGE_KEY = 'saas-kit.locale';

function metaFor(code: string): LocaleMeta {
  return (
    SUPPORTED_LOCALES.find((locale) => locale.code === code) ?? EN_LOCALE
  );
}

/** Direction for a locale code; falls back to ltr for anything unknown. */
export function directionFor(code: string): TextDirection {
  return metaFor(code).dir;
}

function readStoredLocale(): AppLocale {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return SUPPORTED_LOCALES.some((locale) => locale.code === stored)
    ? (stored as AppLocale)
    : DEFAULT_LOCALE;
}

/** Reflect the active locale onto <html> so CSS logical properties,
 *  Tailwind's rtl/ltr variants, and assistive tech all pick up direction. */
function applyDocumentLocale(code: string): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.lang = code;
  root.dir = directionFor(code);
}

void i18n.use(initReactI18next).init({
  resources: {
    en: { common: enCommon },
    ar: { common: arCommon },
  },
  lng: readStoredLocale(),
  fallbackLng: DEFAULT_LOCALE,
  supportedLngs: SUPPORTED_LOCALES.map((locale) => locale.code),
  defaultNS: 'common',
  // When a key isn't found in the active feature namespace (e.g. `auth`),
  // fall back to `common` before returning the raw key. Required so error
  // codes like `errors.auth.invalid_credentials` resolve from any feature
  // page that calls useTranslation('<feature>'). Without this, t() returns
  // the key string and the user sees `errors.auth.invalid_credentials`
  // verbatim.
  fallbackNS: 'common',
  ns: ['common'],
  interpolation: { escapeValue: false },
  debug: import.meta.env.DEV,
});

// Keep <html dir/lang> in sync with the active locale, now and on change.
applyDocumentLocale(i18n.language);
i18n.on('languageChanged', applyDocumentLocale);

/** Switch the active locale and remember it for the next visit. */
export function setLocale(code: AppLocale): void {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(STORAGE_KEY, code);
  }
  void i18n.changeLanguage(code);
}

export function registerFeatureI18n(
  namespace: string,
  resources: Record<string, Record<string, unknown>>,
): void {
  for (const [locale, bundle] of Object.entries(resources)) {
    i18n.addResourceBundle(locale, namespace, bundle, true, true);
  }
}

export default i18n;
